import * as React from 'react';
import moment from 'moment';

import {CardBackgroundRepollState} from 'apps/boards/src/dashboards-app/types';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {LastUpdatedReact} from 'common/components/last-updated';
import {SpinnerReact} from 'common/components/spinner';
import {SpinnerColor, SpinnerSize} from 'common/components/spinner/types';
import {SvgIconReact} from 'common/components/svg-icon';
import {emitInfraTrackEvent} from 'common/util/dom';
import {react2panel} from 'common/util/react2panel';
import {PERFORMANCE_EVENT_PREFIX} from 'common/util/timing';

import './index.styl';

const REFRESH_CLICKED_EVENT = `refresh clicked`;

export interface RefreshButtonJsxProps {
  compareToLastUpdated: (lastUpdated: string) => boolean;
  lastUpdated: string;
  onRefresh: (useQueryApiCache: boolean) => void;
  repollState: CardBackgroundRepollState;
  trackingProps: Record<string, any>;
}

const RefreshButton = (props: RefreshButtonJsxProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const icon =
    props.repollState === CardBackgroundRepollState.Pending ? (
      <SpinnerReact color={SpinnerColor.Blue} size={SpinnerSize.Small} />
    ) : (
      <SvgIconReact height={`4px`} icon={`refresh`} style={{height: `4px`}} />
    );

  const onClick = (ev: MouseEvent | React.MouseEvent) => {
    if (ev instanceof MouseEvent) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
    } else {
      ev.stopPropagation();
    }
    const useQueryCache = props.repollState === CardBackgroundRepollState.Ready;

    const trackingProps: Record<string, any> = {
      ...props.trackingProps,
      'repoll state': props.repollState,
      'age of previous results (min)': moment().diff(moment(props.lastUpdated), `minutes`),
      use_query_cache: useQueryCache,
    };
    if (props.trackingProps.project_id) {
      trackingProps.distinct_id = props.trackingProps.project_id;
    }
    if (containerRef?.current) {
      emitInfraTrackEvent(containerRef.current, {
        eventName: REFRESH_CLICKED_EVENT,
        eventPrefix: PERFORMANCE_EVENT_PREFIX,
        props: trackingProps,
      });
    }

    props.onRefresh(useQueryCache);
  };

  const shouldShowRefresh = () => {
    switch (props.repollState) {
      case CardBackgroundRepollState.Pending:
        return true;
      case CardBackgroundRepollState.Ready:
        // onRefresh handlers may clear the previous lastUpdated value while the new card result is loading
        // to avoid displaying stale data. In this case, we should hide the refresh button.
        return Boolean(props.lastUpdated);
      case CardBackgroundRepollState.None:
        return props.lastUpdated ? props.compareToLastUpdated(props.lastUpdated) : false;
      default:
        return false;
    }
  };

  return shouldShowRefresh() ? (
    <div ref={containerRef} className={`refresh-controls`}>
      <ButtonReact
        className={`refresh-button`}
        disabled={props.repollState === CardBackgroundRepollState.Pending}
        theme={ButtonTheme.Tertiary}
        onClick={(ev) => onClick(ev)}
      >
        {icon}
        <LastUpdatedReact isCompact lastRefreshDate={props.lastUpdated} refreshIntervalMs={1000} />
      </ButtonReact>
    </div>
  ) : (
    <></>
  );
};

export const RefreshButtonEl = react2panel(`mp-refresh-button`, RefreshButton);
export const RefreshButtonReact = RefreshButton;
